import { create } from 'zustand';

/** Parse one raw SSE event string into { type, data } */
function parseSseEvent(eventStr) {
  const lines = eventStr.split('\n').filter((l) => l.trim());
  let eventType = '',
    eventData = '';
  for (const line of lines) {
    if (line.startsWith('event: ')) eventType = line.slice(7).trim();
    else if (line.startsWith('data: ')) eventData = line.slice(6).trim();
  }
  if (!eventType || !eventData) return null;
  try {
    return { type: eventType, data: JSON.parse(eventData) };
  } catch {
    return null;
  }
}

/**
 * Store for the website-finder SSE stream.
 * Persists results across tab switches so the user doesn't lose progress.
 */
export const useWebsiteFinderStore = create((set, get) => ({
  // ── Stream state ──────────────────────────────────────────────────────────
  running: false,
  total: 0,
  processed: 0,
  results: [], // Array of { authority, candidates, error }
  statusMessage: '',
  error: null,
  events: [], // Recent progress / warning messages for UI feedback

  // ── Review state ──────────────────────────────────────────────────────────
  accepted: {}, // { authorityId: url } — accepted URL per authority
  skipped: {}, // { authorityId: true }

  // ── Internal ──────────────────────────────────────────────────────────────
  controller: null,

  // ── Actions ───────────────────────────────────────────────────────────────

  /**
   * Start the SSE website-finder stream.
   * @param {Object} filters - { state, type, limit, delayMs }
   */
  start: async (filters) => {
    const { running, controller: existing } = get();
    if (running && existing) existing.abort();

    const controller = new AbortController();
    set({
      running: true,
      total: 0,
      processed: 0,
      results: [],
      accepted: {},
      skipped: {},
      statusMessage: 'Connecting to website finder…',
      error: null,
      events: [],
      controller,
    });

    try {
      const token = localStorage.getItem('accessToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const response = await fetch('/api/scrapers/website-finder', {
        method: 'POST',
        headers,
        body: JSON.stringify(filters),
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (!response.body) throw new Error('Website finder returned no response body');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const eventStr of events) {
          const evt = parseSseEvent(eventStr);
          if (!evt) continue;

          if (evt.type === 'start') {
            set((s) => ({
              total: evt.data.total,
              statusMessage: evt.data.message || `Queued ${evt.data.total} authorities for review.`,
              events: evt.data.message ? [...s.events, evt.data.message].slice(-8) : s.events,
            }));
          } else if (evt.type === 'progress') {
            set((s) => ({
              statusMessage: evt.data.message || 'Working…',
              events: evt.data.message ? [...s.events, evt.data.message].slice(-8) : s.events,
            }));
          } else if (evt.type === 'warning') {
            set((s) => ({
              statusMessage: evt.data.message || s.statusMessage,
              events: evt.data.message
                ? [...s.events, `Warning: ${evt.data.message}`].slice(-8)
                : s.events,
            }));
          } else if (evt.type === 'result') {
            set((s) => ({
              results: [...s.results, evt.data],
              processed: evt.data.index + 1,
              statusMessage: evt.data.error
                ? `Checked ${evt.data.authority?.name || 'authority'} — no result (${evt.data.error}).`
                : `Checked ${evt.data.authority?.name || 'authority'}.`,
            }));
          } else if (evt.type === 'complete') {
            set((s) => ({
              running: false,
              controller: null,
              statusMessage:
                evt.data.message ||
                `Discovery complete. ${evt.data.withCandidates || 0} authorities had candidates.`,
              events: evt.data.message ? [...s.events, evt.data.message].slice(-8) : s.events,
            }));
          } else if (evt.type === 'error') {
            set((s) => ({
              running: false,
              controller: null,
              error: evt.data.message || 'Website finder failed.',
              statusMessage: evt.data.message || 'Website finder failed.',
              events: evt.data.message
                ? [...s.events, `Error: ${evt.data.message}`].slice(-8)
                : s.events,
            }));
          }
        }
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('[WebsiteFinder] SSE error:', err);
        set((s) => ({
          error: err.message,
          statusMessage: err.message || 'Website finder failed.',
          events: [...s.events, `Error: ${err.message}`].slice(-8),
        }));
      }
    } finally {
      set((s) => ({
        running: false,
        controller: null,
        statusMessage: s.error ? s.statusMessage : s.statusMessage || 'Website finder stopped.',
      }));
    }
  },

  stop: () => {
    const { controller } = get();
    if (controller) controller.abort();
    set((s) => ({
      running: false,
      controller: null,
      statusMessage: 'Discovery stopped by user.',
      events: [...s.events, 'Discovery stopped by user.'].slice(-8),
    }));
  },

  /** Mark an authority as accepted with a chosen URL */
  accept: (authorityId, url) => {
    set((s) => ({ accepted: { ...s.accepted, [authorityId]: url } }));
  },

  /** Mark an authority as skipped */
  skip: (authorityId) => {
    set((s) => ({ skipped: { ...s.skipped, [authorityId]: true } }));
  },

  /** Un-skip (undo skip) */
  unskip: (authorityId) => {
    set((s) => {
      const next = { ...s.skipped };
      delete next[authorityId];
      return { skipped: next };
    });
  },

  /** Un-accept (undo acceptance) */
  unaccept: (authorityId) => {
    set((s) => {
      const next = { ...s.accepted };
      delete next[authorityId];
      return { accepted: next };
    });
  },

  /** Clear all results and reset */
  reset: () => {
    const { controller } = get();
    if (controller) controller.abort();
    set({
      running: false,
      total: 0,
      processed: 0,
      results: [],
      accepted: {},
      skipped: {},
      statusMessage: '',
      error: null,
      events: [],
      controller: null,
    });
  },
}));
